import { useMutation, useQuery } from '@tanstack/react-query';
import fetcher, { FleetPaginated } from '../fetcher';
import type {
	QcPdSearchRequest,
	QcPdSearchItem,
	QcPdMainDetailResponse,
	QcPdApplicationDetailResponse,
	QcPdAssetDetailResponse,
	QcPdFinancialDetailResponse,
	QcPdInsuranceDetailResponse,
	QcPdIncentiveDetailResponse,
	QcCustomerDetailResponse,
	SaveQcCustomerRequest,
	SaveQcPdApplicationRequest,
	SaveQcPdAssetRequest,
	SaveQcPdFinancialRequest,
	SaveQcPdInsuranceRequest,
	SaveQcPdIncentiveRequest,
	SubmitQcPdRequest,
	SubmitQcPdResponse,
} from './qc-pra-disbursement-types';

// ─── Search Options ────────────────────────────────────────────────────────

export type QcPdSearchOptionsResponse = {
	statusOptions?: { id?: string | null; value?: string | null }[] | null;
	searchByOptions?: { id?: string | null; value?: string | null }[] | null;
};

export const getQcPdSearchOptions = () =>
	fetcher<QcPdSearchOptionsResponse>({
		api: '/quality-control-pra-disbursement/get-search-options',
		method: 'GET',
	});

export const useQcPdSearchOptions = () =>
	useQuery({
		queryKey: ['getQcPdSearchOptions'] as const,
		queryFn: () => getQcPdSearchOptions(),
	});

// ─── Search ────────────────────────────────────────────────────────────────

export const searchQcPd = (params: QcPdSearchRequest) =>
	fetcher<FleetPaginated<QcPdSearchItem>>({
		api: '/quality-control-pra-disbursement/search',
		body: params,
	});

export const useQcPdSearch = (params: QcPdSearchRequest) =>
	useQuery({
		queryKey: ['searchQcPd', params] as const,
		queryFn: () => searchQcPd(params),
	});

// ─── Main Detail ───────────────────────────────────────────────────────────

export const getQcPdMainDetail = (purchaseOrderNo: string) =>
	fetcher<QcPdMainDetailResponse>({
		api: '/quality-control-pra-disbursement/get-main-detail',
		body: { purchaseOrderNo },
	});

export const useQcPdMainDetail = (params: { purchaseOrderNo: string }) =>
	useQuery({
		queryKey: ['getQcPdMainDetail', params] as const,
		queryFn: () => getQcPdMainDetail(params.purchaseOrderNo),
		enabled: !!params.purchaseOrderNo,
	});

// ─── Customer Detail ───────────────────────────────────────────────────────

export const getQcPdCustomerDetail = (purchaseOrderNo: string) =>
	fetcher<QcCustomerDetailResponse>({
		api: '/quality-control-pra-disbursement/get-customer-detail',
		body: { purchaseOrderNo },
	});

export const useQcPdCustomerDetail = (params: { purchaseOrderNo: string }) =>
	useQuery({
		queryKey: ['getQcPdCustomerDetail', params] as const,
		queryFn: () => getQcPdCustomerDetail(params.purchaseOrderNo),
		enabled: !!params.purchaseOrderNo,
	});

// ─── Application Detail ────────────────────────────────────────────────────

export const getQcPdApplicationDetail = (purchaseOrderNo: string) =>
	fetcher<QcPdApplicationDetailResponse>({
		api: '/quality-control-pra-disbursement/get-application-detail',
		body: { purchaseOrderNo },
	});

export const useQcPdApplicationDetail = (params: { purchaseOrderNo: string }) =>
	useQuery({
		queryKey: ['getQcPdApplicationDetail', params] as const,
		queryFn: () => getQcPdApplicationDetail(params.purchaseOrderNo),
		enabled: !!params.purchaseOrderNo,
	});

// ─── Asset Detail ──────────────────────────────────────────────────────────

export const getQcPdAssetDetail = (purchaseOrderNo: string) =>
	fetcher<QcPdAssetDetailResponse>({
		api: '/quality-control-pra-disbursement/get-asset-detail',
		body: { purchaseOrderNo },
	});

export const useQcPdAssetDetail = (params: { purchaseOrderNo: string }) =>
	useQuery({
		queryKey: ['getQcPdAssetDetail', params] as const,
		queryFn: () => getQcPdAssetDetail(params.purchaseOrderNo),
		enabled: !!params.purchaseOrderNo,
	});

// ─── Financial Detail ──────────────────────────────────────────────────────

export const getQcPdFinancialDetail = (purchaseOrderNo: string) =>
	fetcher<QcPdFinancialDetailResponse>({
		api: '/quality-control-pra-disbursement/get-financial-detail',
		body: { purchaseOrderNo },
	});

export const useQcPdFinancialDetail = (params: { purchaseOrderNo: string }) =>
	useQuery({
		queryKey: ['getQcPdFinancialDetail', params] as const,
		queryFn: () => getQcPdFinancialDetail(params.purchaseOrderNo),
		enabled: !!params.purchaseOrderNo,
	});

// ─── Insurance Detail ──────────────────────────────────────────────────────

export const getQcPdInsuranceDetail = (purchaseOrderNo: string) =>
	fetcher<QcPdInsuranceDetailResponse>({
		api: '/quality-control-pra-disbursement/get-insurance-detail',
		body: { purchaseOrderNo },
	});

export const useQcPdInsuranceDetail = (params: { purchaseOrderNo: string }) =>
	useQuery({
		queryKey: ['getQcPdInsuranceDetail', params] as const,
		queryFn: () => getQcPdInsuranceDetail(params.purchaseOrderNo),
		enabled: !!params.purchaseOrderNo,
	});

// ─── Incentive Detail ──────────────────────────────────────────────────────

export const getQcPdIncentiveDetail = (purchaseOrderNo: string) =>
	fetcher<QcPdIncentiveDetailResponse>({
		api: '/quality-control-pra-disbursement/get-incentive-detail',
		body: { purchaseOrderNo },
	});

export const useQcPdIncentiveDetail = (params: { purchaseOrderNo: string }) =>
	useQuery({
		queryKey: ['getQcPdIncentiveDetail', params] as const,
		queryFn: () => getQcPdIncentiveDetail(params.purchaseOrderNo),
		enabled: !!params.purchaseOrderNo,
	});

// ─── Save Customer ─────────────────────────────────────────────────────────

export const saveQcPdCustomer = (payload: SaveQcCustomerRequest) =>
	fetcher<void>({
		api: '/quality-control-pra-disbursement/save-customer',
		body: payload,
	});

export const useSaveQcPdCustomer = () =>
	useMutation({ mutationFn: saveQcPdCustomer });

// ─── Save Application ──────────────────────────────────────────────────────

export const saveQcPdApplication = (payload: SaveQcPdApplicationRequest) =>
	fetcher<void>({
		api: '/quality-control-pra-disbursement/save-application',
		body: payload,
	});

export const useSaveQcPdApplication = () =>
	useMutation({ mutationFn: saveQcPdApplication });

// ─── Save Asset ────────────────────────────────────────────────────────────

export const saveQcPdAsset = (payload: SaveQcPdAssetRequest) =>
	fetcher<void>({
		api: '/quality-control-pra-disbursement/save-asset',
		body: payload,
	});

export const useSaveQcPdAsset = () =>
	useMutation({ mutationFn: saveQcPdAsset });

// ─── Save Financial ────────────────────────────────────────────────────────

export const saveQcPdFinancial = (payload: SaveQcPdFinancialRequest) =>
	fetcher<void>({
		api: '/quality-control-pra-disbursement/save-financial',
		body: payload,
	});

export const useSaveQcPdFinancial = () =>
	useMutation({ mutationFn: saveQcPdFinancial });

// ─── Save Insurance ────────────────────────────────────────────────────────

export const saveQcPdInsurance = (payload: SaveQcPdInsuranceRequest) =>
	fetcher<void>({
		api: '/quality-control-pra-disbursement/save-insurance',
		body: payload,
	});

export const useSaveQcPdInsurance = () =>
	useMutation({ mutationFn: saveQcPdInsurance });

// ─── Save Incentive ────────────────────────────────────────────────────────

export const saveQcPdIncentive = (payload: SaveQcPdIncentiveRequest) =>
	fetcher<void>({
		api: '/quality-control-pra-disbursement/save-incentive',
		body: payload,
	});

export const useSaveQcPdIncentive = () =>
	useMutation({ mutationFn: saveQcPdIncentive });

// ─── Submit ────────────────────────────────────────────────────────────────

export const submitQcPd = (payload: SubmitQcPdRequest) =>
	fetcher<SubmitQcPdResponse>({
		api: '/quality-control-pra-disbursement/submit',
		body: payload,
	});

export const useSubmitQcPd = () =>
	useMutation({ mutationFn: submitQcPd });
